import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import { useAppDispatch } from '@application/store/useStore';
import { createPickupLocationVehicleAsync, updatePickupLocationVehicleAsync } from '@application/features/pickupLocationVehicle/pickupLocationVehicleThunk';
import { FamilyFestCreateVehicleAsync, FamilyFestUpdateVehicleAsync } from '@application/features/familyFest/familyFestPickupLocationVehicleThunk';
const NewVehicleDialog = ({ open, onClose, onSuccess, locationId, vehicleToEdit, isFamilyFest }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const dispatch = useAppDispatch();
    const isEditMode = !!vehicleToEdit;
    const { control, handleSubmit, watch, reset, formState: { errors, isSubmitting } } = useForm({
        defaultValues: {
            vehicleNumber: vehicleToEdit?.vehicleNumber || '',
            carrierName: vehicleToEdit?.carrierName || '',
            driverNumber: vehicleToEdit?.driverNumber || '',
            totalCapacity: vehicleToEdit?.totalCapacity || '',
            wheelchairSpots: vehicleToEdit?.wheelchairSpots || ''
        }
    });
    // Reset form when dialog opens or vehicleToEdit changes
    React.useEffect(() => {
        if (open) {
            if (vehicleToEdit) {
                reset({
                    vehicleNumber: vehicleToEdit.vehicleNumber || '',
                    carrierName: vehicleToEdit.carrierName || '',
                    driverNumber: vehicleToEdit.driverNumber || '',
                    totalCapacity: vehicleToEdit.totalCapacity || '',
                    wheelchairSpots: vehicleToEdit.wheelchairSpots || ''
                });
            }
            else {
                reset({
                    vehicleNumber: '',
                    carrierName: '',
                    driverNumber: '',
                    totalCapacity: '',
                    wheelchairSpots: ''
                });
            }
        }
    }, [open, vehicleToEdit, reset]);
    const handleCancel = () => {
        reset();
        onClose();
    };
    const totalCapacityValue = watch('totalCapacity');
    const onSubmit = (values) => {
        const parsedTotalCapacity = Number(values.totalCapacity);
        const parsedWheelchairSpots = Number(values.wheelchairSpots);
        if (parsedWheelchairSpots > parsedTotalCapacity) {
            toast.error('Wheelchair spots cannot exceed total capacity.');
            return;
        }
        if (isEditMode && vehicleToEdit) {
            if (isFamilyFest) {
                const request = {
                    id: vehicleToEdit.id,
                    vehicleNumber: values.vehicleNumber,
                    carrierName: values.carrierName,
                    driverNumber: values.driverNumber,
                    totalCapacity: parsedTotalCapacity,
                    wheelchairSpots: parsedWheelchairSpots || 0,
                    coordinatorUserId: vehicleToEdit.coordinatorUserId || undefined
                };
                dispatch(FamilyFestUpdateVehicleAsync(request))
                    .unwrap()
                    .then((response) => {
                    toast.success(t('events.eventDetails.transportTab.vehicleUpdatedSuccessfully'));
                    reset();
                    onClose();
                    onSuccess?.();
                })
                    .catch((error) => {
                    const errorMessage = error?.message || t('events.eventDetails.transportTab.vehicleUpdateFailed');
                    toast.error(errorMessage);
                });
            }
            else {
                const request = {
                    id: vehicleToEdit.id,
                    vehicleNumber: values.vehicleNumber,
                    carrierName: values.carrierName,
                    driverNumber: values.driverNumber,
                    totalCapacity: parsedTotalCapacity,
                    wheelchairSpots: parsedWheelchairSpots || 0,
                    coordinatorUserId: vehicleToEdit.coordinatorUserId || null
                };
                dispatch(updatePickupLocationVehicleAsync(request))
                    .unwrap()
                    .then((response) => {
                    if (response.isSuccess) {
                        toast.success(response.message || t('events.eventDetails.transportTab.vehicleUpdatedSuccessfully'));
                        reset();
                        onClose();
                        onSuccess?.();
                    }
                    else {
                        toast.error(response.message || t('events.eventDetails.transportTab.vehicleUpdateFailed'));
                    }
                })
                    .catch((error) => {
                    const errorMessage = error?.message || t('events.eventDetails.transportTab.vehicleUpdateFailed');
                    toast.error(errorMessage);
                });
            }
        }
        else {
            if (isFamilyFest) {
                const request = {
                    pickupLocationId: String(locationId),
                    vehicleNumber: values.vehicleNumber,
                    carrierName: values.carrierName,
                    driverNumber: values.driverNumber,
                    totalCapacity: parsedTotalCapacity,
                    wheelchairSpots: parsedWheelchairSpots || 0,
                    coordinatorUserId: undefined
                };
                dispatch(FamilyFestCreateVehicleAsync(request))
                    .unwrap()
                    .then(() => {
                    toast.success(t('events.eventDetails.transportTab.vehicleCreatedSuccessfully'));
                    reset();
                    onClose();
                    onSuccess?.();
                })
                    .catch((error) => {
                    const errorMessage = error?.message || t('events.eventDetails.transportTab.vehicleCreationFailed');
                    toast.error(errorMessage);
                });
            }
            else {
                const request = {
                    pickupLocationId: String(locationId),
                    vehicleNumber: values.vehicleNumber,
                    carrierName: values.carrierName,
                    driverNumber: values.driverNumber,
                    totalCapacity: parsedTotalCapacity,
                    wheelchairSpots: parsedWheelchairSpots || 0,
                    coordinatorUserId: null
                };
                dispatch(createPickupLocationVehicleAsync(request))
                    .unwrap()
                    .then(() => {
                    toast.success(t('events.eventDetails.transportTab.vehicleCreatedSuccessfully'));
                    reset();
                    onClose();
                    onSuccess?.();
                })
                    .catch((error) => {
                    const errorMessage = error?.message || t('events.eventDetails.transportTab.vehicleCreationFailed');
                    toast.error(errorMessage);
                });
            }
        }
    };
    const formContent = (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, children: [_jsx(ControlledFilledTextField, { bg: 'grey8', name: 'vehicleNumber', control: control, label: t('events.eventDetails.transportTab.vehicleNumber'), placeholder: t('events.eventDetails.transportTab.vehicleNumber'), rules: {
                    required: t('validations.required', {
                        field: t('events.eventDetails.transportTab.vehicleNumber')
                    })
                }, errorMessage: errors.vehicleNumber?.message }), isFamilyFest && (_jsxs(_Fragment, { children: [_jsx(ControlledFilledTextField, { bg: 'grey8', name: 'carrierName', control: control, label: t('events.eventDetails.transportTab.carrierName') || 'Carrier Name', placeholder: t('events.eventDetails.transportTab.carrierName') || 'Carrier Name', errorMessage: errors.carrierName?.message }), _jsx(ControlledFilledTextField, { bg: 'grey8', name: 'driverNumber', control: control, label: t('events.eventDetails.transportTab.driverNumber') || 'Driver Number', placeholder: t('events.eventDetails.transportTab.driverNumber') || 'Driver Number', errorMessage: errors.driverNumber?.message })] })), _jsx(ControlledFilledTextField, { bg: 'grey8', name: 'totalCapacity', control: control, label: t('events.eventDetails.transportTab.totalSeats'), placeholder: t('events.eventDetails.transportTab.totalSeats'), type: 'number', slotProps: {
                    input: {
                        inputProps: { step: 1, min: 1, inputMode: 'numeric', pattern: '[0-9]*' }
                    }
                }, rules: {
                    required: t('validations.required', {
                        field: t('events.eventDetails.transportTab.totalSeats')
                    }),
                    min: {
                        value: 1,
                        message: t('validations.min', {
                            field: t('events.eventDetails.transportTab.totalSeats'),
                            min: 1
                        })
                    },
                    validate: (value) => Number.isInteger(Number(value)) ||
                        t('validations.wholeNumberOnly') ||
                        'Total capacity must be a whole number.'
                }, errorMessage: errors.totalCapacity?.message }), _jsx(ControlledFilledTextField, { bg: 'grey8', name: 'wheelchairSpots', control: control, label: t('events.eventDetails.transportTab.wheelChair'), placeholder: t('events.eventDetails.transportTab.wheelChair'), type: 'number', slotProps: {
                    input: {
                        inputProps: { step: 1, min: 0, inputMode: 'numeric', pattern: '[0-9]*' }
                    }
                }, rules: {
                    required: t('validations.required', {
                        field: t('events.eventDetails.transportTab.wheelChair')
                    }),
                    min: {
                        value: 0,
                        message: t('validations.min', {
                            field: t('events.eventDetails.transportTab.wheelChair'),
                            min: 0
                        })
                    },
                    validate: (value) => (Number.isInteger(Number(value)) && Number(value) <= Number(totalCapacityValue || 0)) ||
                        (Number.isInteger(Number(value))
                            ? t('events.eventDetails.transportTab.wheelchairSpotsExceedTotalSeats') ||
                                'Wheelchair spots cannot exceed total capacity.'
                            : t('validations.wholeNumberOnly') || 'Wheelchair spots must be a whole number.')
                }, errorMessage: errors.wheelchairSpots?.message })] }));
    const dialogActions = (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'outlined', color: 'inherit', onClick: handleCancel, size: 'large', fullWidth: true, disabled: isSubmitting, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'inherit', onClick: handleSubmit(onSubmit), size: 'large', fullWidth: true, disabled: isSubmitting, children: isEditMode ? t('actions.save') : t('actions.createNow') })] }));
    if (isSmallScreen) {
        return (_jsx(BDrawer, { open: open, onClose: handleCancel, title: isEditMode
                ? t('events.eventDetails.transportTab.editVehicle')
                : t('events.eventDetails.transportTab.newVehicle'), anchor: 'bottom', showBottomBar: true, dialogActions: dialogActions, children: _jsx(Box, { sx: { p: 2 }, children: formContent }) }));
    }
    return (_jsx(CustomDialog, { open: open, onClose: handleCancel, titleWithIcon: isEditMode
            ? t('events.eventDetails.transportTab.editVehicle')
            : t('events.eventDetails.transportTab.newVehicle'), titleIcon: '/assets/icons/setting-check-icon.svg', maxWidth: 'sm', backgroundVariant: 'white', closeIcon: true, actions: dialogActions, children: _jsx(Box, { p: 1.5, sx: {
                maxHeight: '65vh',
                overflowY: 'auto'
            }, children: formContent }) }));
};
export default NewVehicleDialog;
